import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth'
import { functionError } from '@/hooks/useMix'
import type { Picks, PhraseState } from './questionBank'

/**
 * Voice seeding: the interview in progress (voice_interviews) and the profile
 * it becomes (voice_profiles, written by the voice-profile function).
 * Migration 20240038 Section B.
 */

export type OnboardingStep = 'places' | 'profile' | 'topics' | 'interview' | 'done'
export type VoicePosition = 'newcomer' | 'reporter' | 'insider'

export interface InterviewAnswer {
  id: string
  q: string
  a: string
  skipped?: boolean
}

/** Emma's offers: once a day at most, three a week, Not now snoozes a day,
 *  Stop reminding me turns them off (voice-ring.js). Times are ms. */
export interface RingNudge {
  off?: boolean
  snooze?: number
  last?: number
  wk?: number
  count?: number
  day?: string
}

/** voice_interviews.ring (migration 20240048). */
export interface RingState {
  skipped?: string[]
  picks?: Picks
  nudge?: RingNudge
  /** Answers the last voice profile was built from (voice-profile writes it). */
  built_n?: number
}
export type { Picks, PhraseState }

export interface VoiceInterview {
  client_id: string
  language: string
  step: OnboardingStep
  question_ids: string[]
  answers: InterviewAnswer[]
  position: number
  ring?: RingState
  completed_at: string | null
  updated_at: string
}

export interface VoiceProfile {
  clientId: string
  language: string
  standing: string | null
  rhythm: string | null
  character: string | null
  phrasesUsed: string[]
  phrasesNever: string[]
  defaultPosition: VoicePosition | null
  traits?: Record<string, unknown>
  version: number
  updatedAt: string
}

export function useVoiceInterview(language = 'en') {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['voice-interview', clientId, language],
    enabled: !!clientId,
    staleTime: Infinity,
    queryFn: async (): Promise<VoiceInterview | null> => {
      const { data, error } = await supabase.from('voice_interviews').select('*')
        .eq('client_id', clientId!).eq('language', language).maybeSingle()
      if (error) throw error
      return (data as VoiceInterview | null) ?? null
    },
  })
}

/** Saves progress. Every field is optional; the row is made on first save. */
export function useSaveInterview(language = 'en') {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<Pick<VoiceInterview, 'step' | 'question_ids' | 'answers' | 'position' | 'ring'>>) => {
      const { error } = await supabase.from('voice_interviews').upsert({
        client_id: clientId!, language, ...patch,
        updated_by: userId, updated_at: new Date().toISOString(),
      }, { onConflict: 'client_id,language' })
      if (error) throw error
    },
    onMutate: (patch) => {
      qc.setQueryData<VoiceInterview | null>(['voice-interview', clientId, language], (old) => ({
        client_id: clientId!, language, step: 'places', question_ids: [], answers: [], position: 0,
        ring: {}, completed_at: null, updated_at: new Date().toISOString(), ...(old ?? {}), ...patch,
      }))
    },
  })
}

export interface ProfileResult {
  profile: VoiceProfile | null
  standing_written: boolean
  coin: boolean
}

/** Runs the voice-profile function: the answers saved so far become the profile. */
export function useBuildVoiceProfile(language = 'en') {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (opts: { applyDefaultPosition?: boolean } = {}): Promise<ProfileResult> => {
      const { data, error } = await supabase.functions.invoke('voice-profile', {
        body: { language, apply_default_position: !!opts.applyDefaultPosition },
      })
      if (error) throw await functionError(error)
      return data as ProfileResult
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['voice-interview', clientId, language] })
      qc.invalidateQueries({ queryKey: ['voice-profile', clientId] })
      qc.invalidateQueries({ queryKey: ['topics', clientId] })
      qc.invalidateQueries({ queryKey: ['coin-case'] })
    },
  })
}
